import { useAutoAnimate } from "@formkit/auto-animate/react";
import { PlusIcon } from "@xola/icons";
import { Button, DatePicker, DatePickerPopover, FormGroup, Input, Label, Switch, Tooltip } from "@xola/ui-kit";
import { useMemoizedFn } from "ahooks";
import clsx from "clsx";
import { DateFormat, formatDate, now } from "common";
import dayjs, { Dayjs } from "dayjs";
import React, { useState } from "react";
import {
    BaseScheduleRepeat,
    BaseScheduleType,
    Experience,
    PriceVariation,
    PurchaseRuleLink,
    Schedule,
    ScheduleDeparture,
    ScheduleOption,
    TimeRange,
    WeekdayCopy,
} from "types";
import { getScheduleSummary } from "../../../helpers/scheduleSummaryHelper";
import { useSeller } from "../../../hooks/useAccount";
import { getAllWeekdays } from "../../../models/Weekday";
import { RadioInput } from "../../Common/RadioInput";
import { SecondaryButton } from "../../Common/SecondaryButton";
import { TimeRangeSelector } from "../../Common/TimeRangeSelector";
import { ManageActionButton } from "../../Dashboard/Roster/ManageActionButton";
import { NewTimeslot, TimeslotTimePicker } from "../../PurchaseModification/TimeslotTimePicker";
import { ExperienceOptionPicker } from "./ExperienceOptionPicker";
import { PriceVariations } from "./PriceVariations";
import {
    ScheduleEditorSlots,
    ScheduleEditorSlotsProps,
    useScheduleEditorSlots,
} from "@/components/Product/SchedulesAvailability/useScheduleEditorSlots";
import { isOptionsEnabled } from "@/models/Experience";

export type BaseFormValues = Record<string, any>;

export interface EditorSchedule<Repeat extends BaseScheduleRepeat = BaseScheduleRepeat>
    extends Omit<Schedule, "repeat"> {
    /** We are allowing to customize the repeat option for `ScheduleEditor` component, so we also need an option to customize the `type` for `repeat` */
    repeat: Repeat;
}

export type ScheduleFormValues<FormValues extends BaseFormValues = BaseFormValues> = EditorSchedule<
    FormValues["repeat"]
> &
    FormValues;

const initialValues: EditorSchedule = {
    name: "",
    type: BaseScheduleType.AVAILABLE,
    start: null,
    end: null,
    days: getAllWeekdays(),
    timeRanges: [],
    repeat: BaseScheduleRepeat.CUSTOM,
    departure: ScheduleDeparture.FIXED,
    allowedPrivacies: [],
    times: [],
};

export interface RepeatOption {
    value: string;
    label: string;
    error: boolean;
    disabled?: boolean;
    tooltip?: string;

    /** Used to customize the content for specific repeat option. If root level "slots" are also provided, they would be also overwritten by this property */
    slots?: Partial<ScheduleEditorSlots>;
}

export interface ScheduleEditorProps<FormValues extends BaseFormValues = BaseFormValues>
    extends ScheduleEditorSlotsProps<FormValues> {
    values?: ScheduleFormValues<FormValues>;
    onChange: (values: ScheduleFormValues<FormValues>) => void;
    inModal?: boolean;
    errors?: {
        timeRanges?: Array<{ startTime?: string; endTime?: string }>;
        dates?: string[];
        times?: string[];
        departure?: string;
        days?: string[];
        start?: string;
        options?: Array<{ optionId?: string }> | string;
    };
    // The Experience Option picker's dropdown shouldn't start in an error state the moment its
    // toggle is flipped on - only once the seller has actually tried to submit. Unset means "always show".
    submitCount?: number;
    showAvailability?: boolean;
    disablePastDays?: boolean;
    blackoutSchedules?: boolean;
    isNotResourceSchedule?: boolean;
    basePriceVariation?: PriceVariation;
    onPriceVariationChange: (priceVariation: PriceVariation, purchaseRuleLink: PurchaseRuleLink) => void;
    disableBlackoutTimeRanges?: boolean;
    returnDateTime?: boolean;
    experience?: Experience;
}

const getModifiers = (dates: string[] = []) => {
    return dates?.length > 0 ? { selected: dates.map((date) => dayjs(date).toDate()) } : {};
};

// TODO: The `inModal` prop should ideally be in ui-kit's Popover.
export const ScheduleEditor = <FormValues extends BaseFormValues = BaseFormValues>({
    values = initialValues as ScheduleFormValues<FormValues>,
    onChange,
    inModal = false,
    errors,
    submitCount,
    showAvailability = true,
    disablePastDays = false,
    blackoutSchedules = false,
    isNotResourceSchedule = true,
    basePriceVariation,
    onPriceVariationChange,
    disableBlackoutTimeRanges = false,
    returnDateTime = false,
    repeatOptions = [],
    slots,
    experience,
}: ScheduleEditorProps<FormValues>) => {
    const [animationParent] = useAutoAnimate<HTMLDivElement>();
    const popoverProps = inModal ? { appendTo: document.body, zIndex: 40, placement: "auto" } : undefined;
    const { timeRanges = [], dates = [], times = [] } = values;
    const [selectedTimeSlot, setSelectedTimeSlot] = useState<number | null>(null);
    const timezoneName = useSeller()?.timezoneName;

    const Slots = useScheduleEditorSlots({ values, slots, repeatOptions });

    // Price & event variations only apply to available (non-resource, non-blackout) schedules
    const canShowScheduleVariations =
        isNotResourceSchedule && !(blackoutSchedules || values.type === BaseScheduleType.UNAVAILABLE);

    type DateType = Date | Dayjs;

    // .tz was removed from date handles because this method caused incorrect behavior of datepicker and for example shows endDate not the selected date but the day before.
    // Сhanges made based on ticket X2-4547

    // returnDateTime is part of packages speicic logic, please do not remove or change it without letting @rush or @khomyakov know

    const handleStartDateChange = (start: DateType) => {
        if (start) {
            const startDate = returnDateTime
                ? dayjs(start).tz().format(DateFormat.DATE_TIME_ISO_START_DATE_SELLER)
                : dayjs(start).format(DateFormat.DATE_ISO);
            onChange({ ...values, start: startDate });
        }
    };

    const handleEndDateChange = (end: DateType) => {
        if (end) {
            const endDate = returnDateTime
                ? dayjs(end).tz().format(DateFormat.DATE_TIME_ISO_END_DATE_SELLER)
                : dayjs(end).format(DateFormat.DATE_ISO);
            onChange({ ...values, end: endDate });
        }
    };

    const isWeekdayChecked = (dayIndex: number) => {
        return values.days?.includes(dayIndex);
    };

    const handleWeekdayChange = (dayIndex: number) => {
        const days = isWeekdayChecked(dayIndex)
            ? values.days?.filter((index) => index !== dayIndex)
            : [...(values.days ?? []), dayIndex];

        onChange({ ...values, days: days && days.length > 0 ? days : getAllWeekdays() });
    };

    const handleNoEndChange = () => {
        onChange({ ...values, end: values.end ? null : values.start ? dayjs.max(now(), dayjs(values.start)) : null });
    };

    const handleOptionsChange = useMemoizedFn((next: ScheduleOption[]) => {
        onChange({ ...values, options: next });
    });

    const handleDeleteTimeRange = (deletedIndex: number) => {
        const { timeRanges = [] } = values;
        onChange({ ...values, timeRanges: timeRanges.filter((timeRange, index) => index !== deletedIndex) });
    };

    const handleAddTimeRangeClick = () => {
        const timeRange = {};
        onChange({ ...values, timeRanges: [...timeRanges, timeRange as TimeRange] });
    };

    const handleTimeRangeChange = (updatedTimeRange: Partial<TimeRange>, updatedIndex: number) => {
        const updatedTimeRanges = timeRanges.map((timeRange, index) => {
            return index === updatedIndex ? updatedTimeRange : timeRange;
        });

        onChange({ ...values, timeRanges: updatedTimeRanges as TimeRange[] });
    };

    const handleAddRemoveDateClick = (date: DateType) => {
        const convertedDate = dayjs(date).format("YYYY-MM-DD");
        if (values?.dates?.includes(convertedDate)) {
            onChange({ ...values, dates: dates.filter((existingDate) => existingDate !== convertedDate) });
        } else {
            onChange({ ...values, dates: [...dates, convertedDate] });
        }
    };

    const handleRepeatChange = (repeat: BaseScheduleRepeat | string) => {
        onChange({ ...values, repeat });
    };

    const handleTypeChange = (type: BaseScheduleType) => {
        onChange({ ...values, type });
    };

    const handleNameChange = (e: any) => {
        onChange({ ...values, name: e.target.value });
    };

    const handleTimeSlotChange = (value: any, index: number) => {
        times[index] = value;
        onChange({ ...values, times });
    };

    const handleTimeSlotAdd = () => {
        setSelectedTimeSlot(null);
        onChange({ ...values, times: [...times, null] });
    };

    const handleDepartureChange = (departure: ScheduleDeparture) => {
        onChange({ ...values, departure });
    };

    const handleTimeSlotRemove = (deletedIndex: number) => {
        onChange({ ...values, times: times.filter((time, index) => index !== deletedIndex) });
    };

    const optionsError = Array.isArray(errors?.options) ? errors?.options[0]?.optionId : errors?.options;
    const optionError = submitCount === 0 ? undefined : optionsError;
    const modifiers = getModifiers(values.dates);

    const DefaultContent = (
        <>
            {values?.repeat === BaseScheduleRepeat.WEEKLY && (
                <>
                    <div className="flex items-center space-x-4">
                        <FormGroup className="flex-1">
                            <Label className={clsx(errors?.start && "!text-danger")}>Start Date {errors?.start}</Label>
                            <DatePickerPopover
                                value={values?.start ? formatDate(values.start) : null}
                                placeholder="Now"
                                popoverProps={popoverProps}
                                disabledDays={(date: DateType) =>
                                    (disablePastDays && dayjs(date) < dayjs().subtract(1, "day")) ||
                                    (values.end ? dayjs(date).isAfter(dayjs(values.end).endOf("day")) : undefined)
                                }
                                month={values.start ? dayjs(values.start).toDate() : null}
                                dateFormat={`${DateFormat.DAY}, ${DateFormat.DATE}`}
                                classNames={{ input: "!pl-8" }}
                                timezoneName={timezoneName}
                                onChange={handleStartDateChange}
                            />
                        </FormGroup>

                        <FormGroup className="flex-1">
                            <Label>End Date</Label>

                            <DatePickerPopover
                                value={values?.end ? formatDate(values.end) : null}
                                placeholder="Forever"
                                popoverProps={popoverProps}
                                disabledDays={[{ before: dayjs(values.start).toDate() }]}
                                month={
                                    values.end
                                        ? dayjs(values.end).toDate()
                                        : values.start
                                        ? dayjs(values.start).toDate()
                                        : null
                                }
                                dateFormat={`${DateFormat.DAY}, ${DateFormat.DATE}`}
                                classNames={{ input: "!pl-8" }}
                                timezoneName={timezoneName}
                                onChange={handleEndDateChange}
                            />
                        </FormGroup>
                    </div>

                    <div className="flex items-center justify-end space-x-2">
                        <Switch
                            size="medium"
                            data-testid="base-schedule-editor-no-end-checkbox"
                            isChecked={!values.end}
                            onChange={handleNoEndChange}
                        />
                        <span>No end</span>
                    </div>

                    <FormGroup>
                        <Label className={clsx(errors?.days && "!text-danger")}>Days of Week</Label>

                        <div className="flex items-center space-x-1">
                            {getAllWeekdays().map((weekday) => (
                                <Button
                                    key={weekday}
                                    className={clsx(
                                        isWeekdayChecked(weekday) && "bg-primary-lighter hover:bg-primary-lighter",
                                        "w-14 text-black focus:ring-0",
                                    )}
                                    color={isWeekdayChecked(weekday) ? "primary" : "secondary"}
                                    variant="outline"
                                    size="small"
                                    onClick={() => handleWeekdayChange(weekday)}
                                >
                                    {WeekdayCopy[weekday]}
                                </Button>
                            ))}
                        </div>
                    </FormGroup>
                </>
            )}

            {values?.repeat === BaseScheduleRepeat.CUSTOM && (
                <DatePicker modifiers={modifiers} onChange={handleAddRemoveDateClick} />
            )}

            {values.type === BaseScheduleType.AVAILABLE && isNotResourceSchedule && (
                <>
                    <div>
                        <FormGroup>
                            <Label>Time Slots</Label>
                            <div className="flex items-center space-x-4">
                                <RadioInput
                                    checked={values.departure === ScheduleDeparture.FIXED}
                                    name="times"
                                    value={ScheduleDeparture.FIXED}
                                    disabled={!!values.id}
                                    onChange={() => handleDepartureChange(ScheduleDeparture.FIXED)}
                                >
                                    <span className={clsx(errors?.times && "!text-danger")}>Fixed Times</span>
                                </RadioInput>
                                <RadioInput
                                    checked={values.departure === ScheduleDeparture.VARIES}
                                    name="times"
                                    value={ScheduleDeparture.VARIES}
                                    disabled={!!values.id}
                                    onChange={() => handleDepartureChange(ScheduleDeparture.VARIES)}
                                >
                                    No Fixed Times
                                </RadioInput>
                            </div>
                        </FormGroup>
                    </div>

                    {values?.departure === ScheduleDeparture.FIXED && (
                        <div className="align-center flex flex-col space-y-2">
                            <div className="flex flex-wrap">
                                {times.map((time, index) => (
                                    <div key={time} onClick={() => setSelectedTimeSlot(time)}>
                                        <TagTimeslot
                                            isOpen={selectedTimeSlot === time}
                                            time={time}
                                            onDelete={() => handleTimeSlotRemove(index)}
                                            onChange={(value) => {
                                                handleTimeSlotChange(value, index);
                                                setSelectedTimeSlot(null);
                                            }}
                                        />
                                    </div>
                                ))}
                            </div>

                            <SecondaryButton
                                size="small"
                                icon={PlusIcon}
                                className="self-start"
                                onClick={handleTimeSlotAdd}
                            >
                                Add Time Slot
                            </SecondaryButton>
                        </div>
                    )}

                    {errors?.departure ?? errors?.times ? (
                        <span className="text-sm font-bold text-danger">{errors.departure ?? errors.times}</span>
                    ) : null}
                </>
            )}

            {(!isNotResourceSchedule || values.type === BaseScheduleType.UNAVAILABLE) && !disableBlackoutTimeRanges && (
                <div ref={animationParent}>
                    {timeRanges.map((timeRange, index) => (
                        <TimeRangeSelector
                            // The index is the only identifier for the time ranges,
                            // so we need to disable the following rule:
                            // eslint-disable-next-line react/no-array-index-key
                            key={index}
                            showLabel={index === 0}
                            value={timeRange}
                            errors={errors?.timeRanges?.[index]}
                            onChange={(value) => handleTimeRangeChange(value, index)}
                            onDelete={() => handleDeleteTimeRange(index)}
                        />
                    ))}

                    <ManageActionButton text="Add Time Range" onClick={handleAddTimeRangeClick} />
                </div>
            )}

            {canShowScheduleVariations ? (
                <PriceVariations
                    schedule={values}
                    basePriceVariation={basePriceVariation}
                    onChange={onPriceVariationChange}
                />
            ) : null}

            {canShowScheduleVariations && experience && isOptionsEnabled(experience) ? (
                <ExperienceOptionPicker
                    experience={experience}
                    value={values.options}
                    error={optionError}
                    onChange={handleOptionsChange}
                />
            ) : null}

            <div className="mt-2">
                <Label>Summary</Label>
                {getScheduleSummary(values)}
            </div>
        </>
    );

    return (
        <div>
            <FormGroup>
                <Label>Name</Label>
                <Input value={values.name} onChange={handleNameChange} />
            </FormGroup>
            {blackoutSchedules || !showAvailability ? null : (
                <FormGroup>
                    <Label>Availability</Label>
                    <div className="flex items-center space-x-4">
                        {values.id ? (
                            <p>{values.type === BaseScheduleType.AVAILABLE ? "Available" : "Unavailable"}</p>
                        ) : (
                            <>
                                <RadioInput
                                    checked={values.type === BaseScheduleType.AVAILABLE}
                                    name="type"
                                    value={BaseScheduleType.AVAILABLE}
                                    disabled={!!values.id}
                                    onChange={() => handleTypeChange(BaseScheduleType.AVAILABLE)}
                                >
                                    Available
                                </RadioInput>
                                <RadioInput
                                    checked={values.type === BaseScheduleType.UNAVAILABLE}
                                    name="type"
                                    value={BaseScheduleType.UNAVAILABLE}
                                    disabled={!!values.id}
                                    onChange={() => handleTypeChange(BaseScheduleType.UNAVAILABLE)}
                                >
                                    Unavailable
                                </RadioInput>
                            </>
                        )}
                    </div>
                </FormGroup>
            )}
            <FormGroup>
                <Label>Dates</Label>
                <div className="flex items-center space-x-3 whitespace-nowrap">
                    <RadioInput
                        checked={values.repeat === BaseScheduleRepeat.WEEKLY}
                        name="repeat"
                        value={BaseScheduleRepeat.WEEKLY}
                        disabled={!!values.id}
                        onChange={() => handleRepeatChange(BaseScheduleRepeat.WEEKLY)}
                    >
                        Date Range
                    </RadioInput>
                    <RadioInput
                        checked={values.repeat === BaseScheduleRepeat.CUSTOM}
                        name="repeat"
                        value={BaseScheduleRepeat.CUSTOM}
                        disabled={!!values.id}
                        onChange={() => handleRepeatChange(BaseScheduleRepeat.CUSTOM)}
                    >
                        <div className="flex items-center space-x-2">
                            <span className={clsx(errors?.dates && "!text-danger")}>Specific Dates</span>
                        </div>
                    </RadioInput>

                    {repeatOptions?.map((option) => (
                        <Tooltip
                            key={option.value}
                            content={option.tooltip ?? ""}
                            disabled={!option.disabled || !option.tooltip}
                        >
                            <RadioInput
                                checked={values.repeat === option.value}
                                name="repeat"
                                value={option.value}
                                disabled={!!option.disabled || !!values.id}
                                onChange={() => handleRepeatChange(option.value)}
                            >
                                <div className="flex items-center space-x-2">
                                    <span className={clsx(option.error && "!text-danger")}>{option.label}</span>
                                </div>
                            </RadioInput>
                        </Tooltip>
                    ))}
                </div>
            </FormGroup>

            {Slots.Prefix ? <Slots.Prefix /> : null}

            {Slots.Content ? <Slots.Content /> : DefaultContent}

            {Slots.Suffix ? <Slots.Suffix /> : null}
        </div>
    );
};

interface TagTimeslotProps {
    time: number | null;
    isOpen: boolean;
    onDelete: () => void;
    onChange: (value: number, time: NewTimeslot | null) => void;
}

const TagTimeslot = ({ time, isOpen, onDelete, onChange }: TagTimeslotProps) => {
    const [value, setValue] = useState(time);

    const handleChange = (value: number | null, time: NewTimeslot | null) => {
        if (value === null) {
            onDelete();
            return;
        }

        setValue(value);
        if (time) {
            onChange(value, time);
        }
    };

    return (
        <TimeslotTimePicker
            showTag
            isOpen={isOpen}
            value={value}
            onChange={handleChange}
            onDelete={onDelete}
            onClickOutside={handleChange}
        />
    );
};
